import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import moment from 'moment'

import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'
import FacilityMultiSelect from '../shared/FacilityMultiSelect'

const DATE_PRESETS = [
  { value: 'current', label: 'Current enrollments only' },
  { value: 'skate_year', label: 'Skate Year (Jul 1 - today)' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Date Range' }
]

function skateYearStart() {
  const today = moment()
  const year = today.month() >= 6 ? today.year() : today.year() - 1
  return moment({ year, month: 6, day: 1 })
}

function calculateDatesFromPreset(preset) {
  const today = moment()

  switch (preset) {
    case 'skate_year':
      return {
        date_from: skateYearStart().format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case 'ytd':
      return {
        date_from: today.clone().startOf('year').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case 'current':
      return { date_from: '', date_to: '' }
    default:
      return null
  }
}

function EnrollmentForm({ form, facilities, onSubmit, isSubmitting, isFacilityScoped = false, demo = false }) {
  const { control, handleSubmit, register, setValue, watch } = form
  const [datePreset, setDatePreset] = useState('current')

  const dateFrom = watch('date_from')
  const dateTo = watch('date_to')

  useEffect(() => {
    if (datePreset !== 'custom') {
      const dates = calculateDatesFromPreset(datePreset)
      if (dates) {
        setValue('date_from', dates.date_from)
        setValue('date_to', dates.date_to)
      }
    }
  }, [datePreset, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        {!isFacilityScoped && (
          <div>
            <Label>Facilities</Label>
            <Controller
              name="facility_ids"
              control={control}
              render={({ field }) => (
                <FacilityMultiSelect facilities={facilities} value={field.value} onChange={field.onChange} />
              )}
            />
            <p className="text-sm text-gray-500 mt-1">Leave empty for all facilities.</p>
          </div>
        )}

        <div>
          <Label htmlFor="date_preset">Date Range</Label>
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {datePreset === 'current' && (
            <p className="text-sm text-gray-500 mt-1">Shows students currently enrolled in active classes.</p>
          )}
          {datePreset !== 'custom' && datePreset !== 'current' && dateFrom && dateTo && (
            <p className="text-sm text-gray-500 mt-1">
              {moment(dateFrom).format('MMM D, YYYY')} - {moment(dateTo).format('MMM D, YYYY')}
            </p>
          )}
        </div>

        {datePreset === 'custom' && (
          <>
            <div>
              <Label htmlFor="date_from">From Date</Label>
              <Input id="date_from" type="date" {...register('date_from')} className="mt-1" />
            </div>

            <div>
              <Label htmlFor="date_to">To Date</Label>
              <Input id="date_to" type="date" {...register('date_to')} className="mt-1" />
            </div>
          </>
        )}

        <div>
          <Label htmlFor="file_format">File Format</Label>
          <Controller
            name="file_format"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xls">Excel (XLS)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div hidden={demo}>
          <Label>Delivery Method</Label>
          <Controller
            name="delivery_method"
            control={control}
            render={({ field }) => (
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email"
                    checked={field.value === 'email' || field.value === 'both'}
                    disabled={field.value === 'email'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'download') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('email')
                      } else if (field.value === 'both') {
                        field.onChange('download')
                      }
                    }}
                  />
                  <Label htmlFor="email" className="font-normal cursor-pointer">
                    Email me when ready
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="download"
                    checked={field.value === 'download' || field.value === 'both'}
                    disabled={field.value === 'download'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'email') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('download')
                      } else if (field.value === 'both') {
                        field.onChange('email')
                      }
                    }}
                  />
                  <Label htmlFor="download" className="font-normal cursor-pointer">
                    Available for download
                  </Label>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Generating Preview...' : 'Preview Report'}
      </Button>
    </form>
  )
}

EnrollmentForm.propTypes = {
  demo: PropTypes.bool,
  form: PropTypes.object.isRequired,
  facilities: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isFacilityScoped: PropTypes.bool
}

export default EnrollmentForm
